import { useState } from "react";
import { Phone, Instagram, Globe, Mail, MapPin, Loader2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { submitContact } from "@/lib/api";

const CARDS = [
  { icon: Phone, label: "Phone", value: "8329031991", href: "tel:+918329031991", testId: "contact-phone" },
  { icon: Instagram, label: "Instagram", value: "@HA_TreasureHouse", href: "https://www.instagram.com/HA_TreasureHouse", testId: "contact-instagram" },
  { icon: Globe, label: "Website", value: "HA-TreasureHouse.com", href: null, testId: "contact-website" },
  { icon: Mail, label: "Email", value: "Contact@ha-treasurehouse.com", href: "mailto:Contact@ha-treasurehouse.com", testId: "contact-email" },
  { icon: MapPin, label: "Location", value: "To be announced", href: null, testId: "contact-location" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) { toast.error("Please add your name and message."); return; }
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-28 lg:py-36 bg-royal-900/40 border-y border-gold/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          eyebrow="Contact Us"
          title={<>STEP INTO <span className="gold-text">THE SPOTLIGHT</span></>}
          sub="HA Treasure House — Center of Excellence for Great Talent"
          testId="contact-heading"
        />
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-4">
            {CARDS.map((c, i) => (
              <Reveal key={c.label} delay={0.05 * i}>
                <a
                  href={c.href || undefined}
                  target={c.href?.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className={`glass-card rounded-md px-6 py-5 flex items-center gap-5 transition-all duration-300 ${c.href ? "hover:border-gold/50 hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}
                  data-testid={c.testId}
                >
                  <c.icon className="text-gold shrink-0" size={22} />
                  <div>
                    <p className="text-[11px] tracking-[0.25em] uppercase text-muted">{c.label}</p>
                    <p className="font-display text-lg text-cream mt-0.5">{c.value}</p>
                  </div>
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <a
                href="https://wa.me/918329031991?text=Hello%20HA%20Treasure%20House%2C%20I%20would%20like%20to%20know%20more."
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#1faa55] px-8 py-4 text-sm font-semibold tracking-widest text-white transition-all duration-300 hover:bg-[#1b9249] hover:shadow-[0_0_30px_rgba(31,170,85,0.4)]"
                data-testid="contact-whatsapp-cta"
              >
                <FaWhatsapp size={20} /> CHAT ON WHATSAPP
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="glass-card rounded-md p-7 sm:p-9 space-y-5" data-testid="contact-form">
              <input value={form.name} onChange={set("name")} placeholder="Your name *" className="input-lux" data-testid="contact-name-input" />
              <input value={form.email} onChange={set("email")} type="email" placeholder="Your email" className="input-lux" data-testid="contact-email-input" />
              <input value={form.phone} onChange={set("phone")} type="tel" placeholder="Your phone" className="input-lux" data-testid="contact-phone-input" />
              <textarea rows={4} value={form.message} onChange={set("message")} placeholder="Your message *" className="input-lux resize-none" data-testid="contact-message-input" />
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-gold text-royal-950 px-8 py-4 text-sm font-semibold tracking-widest transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] disabled:opacity-60 inline-flex items-center justify-center gap-3"
                data-testid="contact-submit-btn"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                SEND MESSAGE
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
